import React, {MouseEvent, ReactNode, useEffect, useState} from 'react';
import {get} from 'lodash';

import CheckboxField from 'components/CheckboxField';
import Typography from 'components/Typography';

import {Order} from 'types';

import {ReactComponent as MorningIcon} from 'assets/icons/morning.svg';
import {ReactComponent as DayIcon} from 'assets/icons/day.svg';
import {ReactComponent as EveningIcon} from 'assets/icons/evening.svg';
import styles from './Ticket.module.scss';
import NoteIcon from 'components/Sidebar/MenuIcon/NoteIcon';
import classNames from 'classnames';
import arrow_bottom from './arrow_bottom.svg';
import {FlagIcon} from 'pages/DeliveryPlanner/icons/FlagIcon';

interface Props extends Order {
  index?: number;
  arrival_time?: string;
  arrival_date?: string;
  new_leaf_order?: string;
  selected?: boolean;
  timePreference?: string;
  isAssigned: boolean;
  onChangeCheckbox: (id: number, checked: boolean, customerId?: number) => void;
  onOrdersClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleNoteClick: () => void;
  showTime?: boolean;
  isPrint?: boolean;
  isError?: boolean;
  toggleDeliveryGroupFlag: (body: {
    request_ids?: number[];
    is_urgent_delivery?: boolean;
  }) => void;
}

// Map time preferences to their corresponding icons
const timePreferenceIconMap: Record<string, ReactNode> = {
  morning: <MorningIcon />,
  day: <DayIcon />,
  evening: <EveningIcon />,
};

function Ticket({
  index,
  id,
  arrival_date,
  timePreference,
  delivery_address,
  arrival_time,
  selected,
  isAssigned,
  customer,
  onChangeCheckbox,
  onOrdersClick,
  handleNoteClick,
  showTime,
  isPrint = false,
  isError,
  orders,
  is_redispatch,
  flag_reason,
  is_urgent_delivery,
  toggleDeliveryGroupFlag,
}: Props) {
  const [isUrgentDelivery, setIsUrgentDelivery] = useState(is_urgent_delivery);
  // Convert 24-hour time format to 12-hour format with AM/PM
  function naming() {
    if (typeof arrival_time === 'string') {
      const [hour, minute] = arrival_time.split(':');
      let hourInt = parseInt(hour, 10);
      let minuteInt = parseInt(minute, 10);

      let period = 'AM';
      if (hourInt >= 12) {
        period = 'PM';
      }

      // Handle midnight (0 hour) and convert to 12-hour format
      if (hourInt === 0) {
        hourInt = 12;
      } else if (hourInt > 12) {
        hourInt -= 12;
      }
      return `${hourInt}:${minuteInt.toString().padStart(2, '0')} ${period}`;
    }
    return '';
  }
  const handleOrderClick = (e: MouseEvent<HTMLDivElement>) => {
    onOrdersClick(e);
  };

  const handleTooltipMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const tooltip = e.currentTarget.querySelector(
      `.${styles.tooltip}`
    ) as HTMLElement;
    if (tooltip && flag_reason) {
      const rect = e.currentTarget.getBoundingClientRect();
      tooltip.style.top = `${rect.top - tooltip.offsetHeight - 8}px`;
      tooltip.style.right = `${window.innerWidth - rect.right}px`;
    }
  };
  // Control ticket expansion state - default open for first item (index 0)
  const [isOpen, setIsOpen] = useState(!index);

  // Auto-collapse tickets when showTime is enabled and not in print mode
  useEffect(() => {
    if (showTime && !isPrint) {
      setIsOpen(false);
    }
  }, [showTime, isPrint]);

  const handleToggleDeliveryGroupFlag = () => {
    if (!toggleDeliveryGroupFlag) return;
    setIsUrgentDelivery(!isUrgentDelivery);
    toggleDeliveryGroupFlag({
      request_ids: orders.map((order) => Number(order.new_leaf_order)),
      is_urgent_delivery: !isUrgentDelivery,
    });
  };
  return (
    <div
      className={classNames(
        styles.root,
        // Add border color for invalid addresses or error states
        ((typeof delivery_address !== 'string' &&
          !delivery_address?.is_valid) ||
          isError) &&
          styles.borderColor
      )}
    >
      {/* Show index and collapse/expand arrow for non-print mode */}
      <div className={styles.rightElement}>
        <div
          className={styles.urgentDeliveryFlag}
          onMouseEnter={handleTooltipMouseEnter}
          onClick={handleToggleDeliveryGroupFlag}
        >
          <FlagIcon
            width={14}
            height={14}
            color={isUrgentDelivery ? '#ef233c' : '#a9a9aa'}
          />
          {flag_reason && <span className={styles.tooltip}>{flag_reason}</span>}
        </div>
        {index && !isPrint && (
          <>
            <span className={styles.index}>{index}</span>
            <img
              onClick={() => setIsOpen((pr) => !pr)}
              alt="arrow bottom"
              src={arrow_bottom}
              className={isOpen ? styles.open : ''}
            />
          </>
        )}
      </div>

      {/* Header section - different layout for print vs screen */}
      {isPrint ? (
        <div className={styles.top}>
          <Typography variant="h4">STOP # {index ? index : ''}</Typography>
          {/* Show delivery time for assigned orders in print mode */}
          {typeof arrival_time === 'string' && isAssigned && showTime && (
            <span className={styles.delivery_time}>
              {' '}
              {'-  '}
              {naming()}
            </span>
          )}
        </div>
      ) : (
        <div
          style={{marginBottom: index && !isPrint ? 0 : 8}}
          className={styles.top}
        >
          {/* Checkbox for order selection */}
          <CheckboxField
            name={`checkbox-${id}`}
            className={styles.checkbox}
            label={`#${id}`}
            checked={selected}
            onChange={() => onChangeCheckbox(id, !selected, customer?.id)}
          />
          {/* Show action buttons when expanded, customer name when collapsed */}
          {isOpen ? (
            <>
              {!isAssigned && (
                <div
                  className={`${styles.orderCountQuad} ${
                    is_redispatch ? styles.redispatch : ''
                  }`}
                  onClick={handleOrderClick}
                >
                  {orders.length}
                </div>
              )}
              {/* Note button only for assigned orders */}
              {isAssigned && (
                <div className={styles.noteButton}>
                  <NoteIcon
                    className={styles.listButton}
                    onClick={handleNoteClick}
                  />
                </div>
              )}
            </>
          ) : (
            // Show customer name when collapsed
            <Typography className={styles.customerEllipsis} variant="p">
              {get(customer, 'user.first_name', '')}{' '}
              {get(customer, 'user.last_name', '')}
            </Typography>
          )}
        </div>
      )}

      {/* Expanded content - show when open or in print mode */}
      {(isOpen || isPrint) && (
        <>
          {/* Customer name - handle both string and object formats */}
          {typeof customer === 'string' ? (
            <Typography className={styles.customer} variant="p">
              {customer}
            </Typography>
          ) : (
            <Typography className={styles.customer} variant="p">
              {get(customer, 'user.first_name', '')}{' '}
              {get(customer, 'user.last_name', '')}
            </Typography>
          )}

          {/* Delivery address - handle both string and object formats */}
          {typeof delivery_address === 'string' ? (
            <Typography className={styles.addressCalc} variant="p">
              {delivery_address}
            </Typography>
          ) : (
            <Typography className={styles.address} variant="p">
              {delivery_address?.address_line_1 || ''}{' '}
              {delivery_address?.address_line_2 || ''} <br />
              {delivery_address?.city}, {delivery_address?.state}{' '}
              {delivery_address?.zipcode}
            </Typography>
          )}

          {/* Footer with time preference icon and delivery time */}
          <div className={styles.footer}>
            {/* Time preference icon */}
            {timePreference && timePreferenceIconMap[timePreference] && (
              <span className={styles.time_preference}>
                {timePreferenceIconMap[timePreference]}
              </span>
            )}
            {/* Delivery time for assigned orders (screen mode) */}
            {typeof arrival_time === 'string' &&
              isAssigned &&
              showTime &&
              !isPrint && (
                <span className={styles.delivery_time}>
                  {arrival_date ? `${arrival_date}  ` : ''} {naming()}
                </span>
              )}
            {/* Unit address for print mode */}
            {!!get(delivery_address, 'unit_address', '') && isPrint && (
              <span
                style={{
                  justifyContent: 'flex-start',
                }}
                className={styles.delivery_time}
              >
                Unit # {get(delivery_address, 'unit_address', '')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Ticket;
